import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerPadding,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { motion } from 'framer-motion';
import { default as React } from 'react';
import { OfertasContext } from '../../context/ofertas-context';
import {
	checkActiveDisclaimer,
	formatCalculatedDisclaimer,
} from '../../helpers/get-disclaimer';
import { capitalizeSentence, formatPrecio } from '../../helpers/ofertas-helper';
import { Oferta } from '../../services/ofertas-service/models/ofertas';

const getMiddleModalidad = (oferta: Oferta) => {
	if (
		oferta.cuotaModalidad &&
		oferta.precioModalidad !== oferta.cuotaModalidad &&
		oferta.precio
	) {
		return <>{oferta.cuotaModalidad + '\u00A0'}</>;
	}
	return;
};

export const getCuotaType = (price: number, oferta?: Oferta) => {
	if (!price) {
		return;
	}

	let priceWithUnits = formatPrecio(price) + '€';

	if (oferta) {
		switch (oferta.cuotaTipo) {
			case 'anual':
				priceWithUnits += '/año';
				break;

			case 'diario':
				priceWithUnits += '/dia';
				break;

			default:
				priceWithUnits += '/mes';
				break;
		}
	}

	return (
		<>
			{priceWithUnits}
			<Text sup>{oferta?.legal}</Text>
		</>
	);
};

const getModalidadType = (price: number, oferta?: Oferta) => {
	if (!price || !oferta) {
		return;
	}

	let modalidadType = '';

	if (oferta && oferta.myWay.activo) {
		modalidadType = ' con My Way';
	}

	if (oferta && oferta.myRenting) {
		modalidadType = ' con My Renting';
	}

	if (oferta && oferta.next) {
		modalidadType = 'con Next';
	}

	if (
		oferta &&
		oferta.calculatedFinance.rawResponse?.Response?.Parameters?.Description
	) {
		modalidadType =
			'con\u00A0' +
			oferta.calculatedFinance.rawResponse?.Response?.Parameters?.Description;
	}

	return <>{modalidadType}</>;
};

export const OfertaPrice = ({
	oferta,
}: {
	oferta: Oferta;
}): React.ReactElement => {
	const { disclaimerManager } = React.useContext(OfertasContext);

	return (
		<Container
			padding={{ top: ContainerPadding.static200 }}
			verticalAlign={'flex-end'}
			wrap={'always'}
		>
			{oferta.calculatedFinance.rawResponse ? (
				<motion.div
					key={JSON.stringify(oferta.id)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1 }}
				>
					<Container
						wrap={{
							[Breakpoints.default]: 'always',
							[Breakpoints.b560]: 'never',
						}}
						verticalAlign={'flex-end'}
					>
						<Text
							textAlign={TextAlignment.end}
							tag={TextTag.span}
							appearance={TokenTextAppearance.copy100}
							color={TokenTextColor.primary}
						>
							Tu opción personalizada
						</Text>
					</Container>

					<Container verticalAlign={'flex-end'}>
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0020,
									[Breakpoints.b560]: ContainerPadding.static100,
									[Breakpoints.b960]: ContainerPadding.dynamic0020,
									[Breakpoints.b1600]: ContainerPadding.static100,
								},
							}}
							verticalAlign={'flex-end'}
							horizontalAlign={'flex-end'}
						>
							<Text
								textAlign={TextAlignment.end}
								tag={TextTag.span}
								appearance={TokenTextAppearance.headline100}
								color={TokenTextColor.primary}
							>
								{'Por\u00A0'}
							</Text>
						</Container>

						<Container wrap={'never'} verticalAlign={'flex-end'}>
							<Container wrap={'never'}>
								<Text
									tag={TextTag.span}
									appearance={TokenTextAppearance.headline300}
									color={TokenTextColor.primary}
									bold
								>
									{oferta.calculatedFinance.summaries?.Full.Rate?.Value}
									€/mes
									<Text sup>
										{checkActiveDisclaimer(
											disclaimerManager
												.getScopedDisclaimerManager()
												.getFooterDisclaimers().reference,
											formatCalculatedDisclaimer(oferta),
										)?.references?.[0].replace('.', '')}
									</Text>
									{'\u00A0'}
								</Text>
							</Container>
						</Container>
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0020,
									[Breakpoints.b560]: ContainerPadding.static100,
									[Breakpoints.b960]: ContainerPadding.dynamic0020,
									[Breakpoints.b1600]: ContainerPadding.static100,
								},
							}}
							verticalAlign={'flex-end'}
							horizontalAlign={'flex-end'}
						>
							<Text
								tag={TextTag.span}
								appearance={TokenTextAppearance.headline100}
								color={TokenTextColor.primary}
							>
								{getModalidadType(oferta.cuota, oferta)}
							</Text>
						</Container>
					</Container>
				</motion.div>
			) : (
				<>
					<Container
						wrap={{
							[Breakpoints.default]: 'always',
							[Breakpoints.b560]: 'never',
						}}
						verticalAlign={'flex-end'}
					>
						<Container wrap={'never'} verticalAlign={'flex-end'}>
							{oferta.precio ? (
								<Container wrap={'never'} verticalAlign={'flex-end'}>
									{oferta.precioModalidad && (
										<Container
											padding={{
												bottom: {
													[Breakpoints.default]: ContainerPadding.dynamic0020,
													[Breakpoints.b560]: ContainerPadding.static100,
													[Breakpoints.b960]: ContainerPadding.dynamic0020,
													[Breakpoints.b1600]: ContainerPadding.static100,
												},
											}}
											verticalAlign={'flex-end'}
											horizontalAlign={'flex-end'}
										>
											<Text
												textAlign={TextAlignment.end}
												tag={TextTag.span}
												appearance={TokenTextAppearance.headline100}
												color={TokenTextColor.primary}
											>
												{capitalizeSentence(oferta.precioModalidad) + '\u00A0'}
											</Text>
										</Container>
									)}
									<Container wrap={'never'} verticalAlign={'flex-end'}>
										<Container wrap={'never'}>
											<Text
												tag={TextTag.span}
												appearance={TokenTextAppearance.headline300}
												color={TokenTextColor.primary}
												bold
											>
												{getCuotaType(oferta.precio)}
											</Text>
											<Text sup bold>
												{checkActiveDisclaimer(
													disclaimerManager
														.getScopedDisclaimerManager()
														.getFooterDisclaimers().reference,
													oferta.legal,
												)?.references?.[0].replace('.', '')}
											</Text>
										</Container>
									</Container>
								</Container>
							) : (
								<Container wrap={'never'} verticalAlign={'flex-end'}>
									{oferta.cuotaModalidad && (
										<Container
											padding={{
												bottom: {
													[Breakpoints.default]: ContainerPadding.dynamic0020,
													[Breakpoints.b560]: ContainerPadding.static100,
													[Breakpoints.b960]: ContainerPadding.dynamic0020,
													[Breakpoints.b1600]: ContainerPadding.static100,
												},
											}}
											verticalAlign={'flex-end'}
											horizontalAlign={'flex-end'}
										>
											<Text
												textAlign={TextAlignment.end}
												tag={TextTag.span}
												appearance={TokenTextAppearance.headline100}
												color={TokenTextColor.primary}
											>
												{capitalizeSentence(oferta.cuotaModalidad)}{' '}
											</Text>
										</Container>
									)}
									<Container
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.dynamic0020,
												[Breakpoints.b560]: ContainerPadding.static100,
												[Breakpoints.b960]: ContainerPadding.dynamic0020,
												[Breakpoints.b1600]: ContainerPadding.static100,
											},
										}}
									>
										<Text
											tag={TextTag.span}
											appearance={TokenTextAppearance.headline300}
											color={TokenTextColor.primary}
											bold
										>
											{parseFloat(oferta.cuota.toFixed(2))
												.toString()
												.replace('.', ',')}
											€/mes
											<Text sup bold>
												{checkActiveDisclaimer(
													disclaimerManager
														.getScopedDisclaimerManager()
														.getFooterDisclaimers().reference,
													oferta.legal,
												)?.references?.[0].replace('.', '')}
											</Text>
											<Text
												tag={TextTag.span}
												appearance={TokenTextAppearance.headline100}
												color={TokenTextColor.primary}
												bold
											>
												{getModalidadType(oferta.cuota, oferta)}
											</Text>
										</Text>
									</Container>
								</Container>
							)}
						</Container>

						{oferta.precio && (
							<Container
								padding={{
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0020,
										[Breakpoints.b560]: ContainerPadding.static100,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
										[Breakpoints.b1600]: ContainerPadding.static100,
									},
								}}
							>
								<BreakpointWrapper
									min={Breakpoints.default}
									max={Breakpoints.b560}
								>
									<Text
										tag={TextTag.span}
										appearance={TokenTextAppearance.copy100}
										color={TokenTextColor.secondary}
										bold={false}
									>
										{'Sujeto a financiación lineal'}
									</Text>
								</BreakpointWrapper>
								<BreakpointWrapper min={Breakpoints.b560}>
									<Text
										tag={TextTag.span}
										appearance={TokenTextAppearance.copy100}
										color={TokenTextColor.secondary}
										bold={false}
									>
										{'\u00A0sujeto a financiación lineal'}
									</Text>
								</BreakpointWrapper>
							</Container>
						)}
					</Container>
					<Container wrap={'never'} verticalAlign={'flex-end'}>
						{oferta.precio && oferta.precioModalidad && oferta.cuota && (
							<Container
								padding={{
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0020,
										[Breakpoints.b560]: ContainerPadding.static100,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
										[Breakpoints.b1600]: ContainerPadding.static100,
									},
								}}
							>
								<Text
									tag={TextTag.span}
									appearance={TokenTextAppearance.headline100}
									color={TokenTextColor.primary}
								>
									{'o\u00A0'}
								</Text>
							</Container>
						)}

						{oferta.precio && (
							<Container
								padding={{
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0020,
										[Breakpoints.b560]: ContainerPadding.static100,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
										[Breakpoints.b1600]: ContainerPadding.static100,
									},
								}}
							>
								<Text
									tag={TextTag.span}
									appearance={TokenTextAppearance.headline100}
									color={TokenTextColor.primary}
								>
									{getMiddleModalidad(oferta)}
								</Text>
							</Container>
						)}

						{oferta.precio && oferta.cuota && (
							<Container
								padding={{
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0020,
										[Breakpoints.b560]: ContainerPadding.static100,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
										[Breakpoints.b1600]: ContainerPadding.static100,
									},
								}}
							>
								<Text
									tag={TextTag.span}
									appearance={TokenTextAppearance.headline100}
									color={TokenTextColor.primary}
									bold
								>
									{parseFloat(oferta.cuota.toFixed(2))
										.toString()
										.replace('.', ',')}
									€/mes
									<Text sup>
										{checkActiveDisclaimer(
											disclaimerManager
												.getScopedDisclaimerManager()
												.getFooterDisclaimers().reference,
											oferta.cuotaLegal,
										)?.references?.[0].replace('.', '')}
									</Text>
									{getModalidadType(oferta.cuota, oferta)}
								</Text>
							</Container>
						)}
					</Container>
				</>
			)}
		</Container>
	);
};
